import { AgentScreen } from "@commerce/agent/AgentScreen";
import { EDITOR_TABS } from "@commerce/agent/settings-diff";
import { LoadingRegion } from "@commerce/components/page";
import { config } from "@repo/config";
import { AsyncBoundary } from "@shared/components/AsyncBoundary";
import { orpc } from "@shared/lib/orpc";
import { dehydrateForLoader, getServerQueryClient } from "@shared/lib/server";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

const agentSearchSchema = z.object({
	/** A shopper message to put in the playground composer. Never sent. */
	ask: z.string().max(500).optional(),
	/** The open tab; absent is the first one. */
	tab: z
		.enum(EDITOR_TABS.map((tab) => tab.id))
		.optional()
		.catch(undefined),
});

export const Route = createFileRoute("/_saas/app/_org/$organizationSlug/agent")(
	{
		validateSearch: agentSearchSchema,
		staticData: { fullBleed: true },
		loader: async ({ context }) => {
			const organizationId = context.organization.id;
			const queryClient = getServerQueryClient();
			await queryClient.ensureQueryData(
				orpc.agentConfig.get.queryOptions({
					input: { organizationId },
				}),
			);
			return {
				organizationId,
				dehydratedState: dehydrateForLoader(queryClient),
			};
		},
		head: () => ({ meta: [{ title: `Configure - ${config.appName}` }] }),
		component: AgentPage,
	},
);

function AgentPage() {
	const { organizationId, dehydratedState } = Route.useLoaderData();
	const { ask, tab } = Route.useSearch();
	const { organizationSlug } = Route.useParams();
	const navigate = Route.useNavigate();
	return (
		<AsyncBoundary
			fallback={<LoadingRegion label="Loading the agent" />}
			dehydratedState={dehydratedState}
		>
			<AgentScreen
				organizationId={organizationId}
				organizationSlug={organizationSlug}
				tab={tab ?? EDITOR_TABS[0].id}
				onTab={(next) =>
					navigate({
						// A new tab puts its own pane back, so a pending ?ask= is done with.
						search: (current) => ({
							...current,
							tab: next === EDITOR_TABS[0].id ? undefined : next,
							ask: undefined,
						}),
						replace: true,
						resetScroll: false,
					})
				}
				ask={ask}
				// Drop ?ask= once the user moves on from it, so a reload doesn't put it back.
				onAskUsed={() =>
					navigate({
						search: (current) => ({ ...current, ask: undefined }),
						replace: true,
						resetScroll: false,
					})
				}
			/>
		</AsyncBoundary>
	);
}
